import { Component, Inject } from '@angular/core';
import { Header } from '../../../../../../../types/src/table';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Asset } from '@aptsi-types';
import { Router } from '@angular/router';
import { AccessFlowService } from '../access-flow.service';
import { AssetService } from '../../asset/asset.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TimeFormatService } from '../../shared/service/time-format.service';
import { KeycloakStateService } from 'src/app/core/service/utility.service';

@Component({
  selector: 'app-generate-access-flow-dialog',
  templateUrl: './generate-access-flow-dialog.component.html',
  styleUrls: ['./generate-access-flow-dialog.component.scss']
})
export class GenerateAccessFlowDialogComponent {

  // ───────────────── Pagination ─────────────────
  currentPage = 1;
  itemsPerPage = 7;

  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;

  // ───────────────── Search ─────────────────
  filterByHeader = 'all';
  searchText = '';

  searchTextChanged = new Subject<string>();

  isSearch = false;

  // ───────────────── Data ─────────────────
  filteredData: Asset[] = [];
  searchedData: Asset[] = [];
  getOrganizationId= this.keycloakStateService.getOrganizationId();
  principalTypeId :String= '';

  // ───────────────── Selection ─────────────────
  selectedEntries: Asset[] = [];

  selectedAssetIds = new Set<string>();

  constructor(
    private router: Router,

    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      message: string;
      digitalId: string;
      path: string;
      isCreate: string;
      organization: string;
      pathoverride: string;
      selectedEntries: any[];
    },

    private accessFlowService: AccessFlowService,
    private assetService: AssetService,
    private timeFormatService: TimeFormatService,
    private keycloakStateService: KeycloakStateService,
    private dialog: MatDialog
  ) {}

  headers: Header[] = [
    {
      name: 'Asset Id',
      property: 'assetId',
      showInDropdown: true,
    },
    {
      name: 'Asset Name',
      property: 'assetName',
      showInDropdown: true,
    },
    {
      name: 'Principle Type',
      property: 'principalTypeName',
      showInDropdown: true,
    }
    
  ];

  // ───────────────── Lifecycle ─────────────────
  async ngOnInit(): Promise<void> {

    // Restore selections during update
    if (this.data?.selectedEntries?.length) {

      this.selectedEntries = [...this.data.selectedEntries];

      this.selectedEntries.forEach(asset => {
        this.selectedAssetIds.add(asset.assetId);
      });
    }

    await this.fetchAssets();

    this.searchTextChanged
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.searchAssets();
      });
  }

  // ───────────────── Pagination ─────────────────
  changePage(page: number): void {

    this.currentPage = page;

    this.totalPages = this.isSearch
      ? this.totalPagesSearch
      : this.totalPagesNormal;

    if (this.isSearch) {
      this.fetchAssetSearch();
    } else {
      this.fetchAssets();
    }
  }

  // ───────────────── Fetch Normal Assets ─────────────────
  async fetchAssets(): Promise<void> {

    try {

      const response = await this.assetService
        .getRemainingAssetsAccessFlow(this.currentPage - 1, this.getOrganizationId)
        .toPromise();

      this.formatAndSetAssets(response, false);

    } catch (error) {

      console.error('Error fetching assets:', error);
    }
  }

  // ───────────────── Fetch Search Assets ─────────────────
  async fetchAssetSearch(): Promise<void> {

    try {

      const response = await this.assetService
        .getRemainingAssetsAccessFlowSearch(
          this.currentPage - 1,
          this.searchText,
          this.filterByHeader,
          this.getOrganizationId
        )
        .toPromise();

      this.formatAndSetAssets(response, true);

    } catch (error) {

      console.error('Error fetching searched assets:', error);
    }
  }

  // ───────────────── Format Data ─────────────────
  private formatAndSetAssets(
    response: any[],
    inSearchMode: boolean
  ): void {

    const formatted = response.map(asset => ({
      ...asset
    }));

    if (inSearchMode) {

      this.searchedData = formatted;

      this.totalPagesSearch =
        response[0]?.totalPages || 0;

    } else {

      this.filteredData = formatted;

      this.totalPagesNormal =
        response[0]?.totalPages || 0;
    }

    this.updatePagination(response);
  }

  // ───────────────── Pagination Metadata ─────────────────
  private updatePagination(response: any[]): void {

    if (
      response.length > 0 &&
      response[0].totalPages
    ) {

      const totalPages = response[0].totalPages;

      const currentPage =
        response[0].currentPage + 1;

      if (this.isSearch) {
        this.totalPagesSearch = totalPages;
      } else {
        this.totalPagesNormal = totalPages;
      }

      this.totalPages = totalPages;
      this.currentPage = currentPage;
    }
  }

  // ───────────────── Selection ─────────────────
  onSelectionChanged(selectedAssets: Asset[]): void {

    const currentPageData = this.isSearch
      ? this.searchedData
      : this.filteredData;

    const currentPageIds = currentPageData.map(
      asset => asset.assetId
    );

    // Remove current page selections first
    currentPageIds.forEach(id => {
      this.selectedAssetIds.delete(id);
    });

    // Add current selected assets
    selectedAssets.forEach(asset => {
      this.selectedAssetIds.add(asset.assetId);
    });

    // Merge all possible assets
    const allAssets = [
      ...this.filteredData,
      ...this.searchedData,
      ...this.selectedEntries
    ];

    // Remove duplicates
    const uniqueAssets = allAssets.filter(
      (asset, index, self) =>
        index === self.findIndex(
          a => a.assetId === asset.assetId
        )
    );

    // Final selected entries
    this.selectedEntries = uniqueAssets.filter(
      asset =>
        this.selectedAssetIds.has(asset.assetId)
    );

    console.log(
      'Updated Selected Entries:',
      this.selectedEntries
    );
  }

  // ───────────────── Search ─────────────────
  onSearchTextChange(searchValue: string): void {

    this.searchTextChanged.next(searchValue);
  }

  async searchAssets(): Promise<void> {

    try {

      if (this.searchText?.trim()) {

        this.isSearch = true;
        this.currentPage = 1;

        if (!this.filterByHeader) {
          this.filterByHeader = 'all';
        }

        // Backend handles:
        // search
        // pagination
        // filtering
        await this.fetchAssetSearch();

      } else {

        this.isSearch = false;
        this.currentPage = 1;

        await this.fetchAssets();
      }

    } catch (error) {

      console.error(
        'Error during search:',
        error
      );
    }
  }

  // ───────────────── Finish ─────────────────
  // ───────────────── Finish ─────────────────
finish(): void {

  const payload = this.selectedEntries.map(asset => ({
    organizationId: this.getOrganizationId,
    assetId: asset.assetId,
    principalTypeId: asset.principalTypeId
  }));

  console.log('Payload:', payload);

  this.assetService
    .generateRemainingAssetsAccessFlow(payload)
    .subscribe(
      () => {
        this.closeDialog();
      },
      error => {
        console.error(
          'Error creating access flow assets:',
          error
        );

        this.closeDialog();
      }
    );
}

  // ───────────────── Close ─────────────────
  closeDialog(): void {

    this.dialog.closeAll();
  }
}